/**
 * FrameBudgetMonitor — sustained-slowness telemetry (plan 13 §8).
 *
 * Fed one timestamp per frame by FreezeSentinel's rAF loop. Every
 * `FRAME_BUDGET_CONFIG.WINDOW_MS` it judges the focused frames of the window;
 * a window that misses its budget (p95 over budget, or several frames over
 * 50 ms) produces ONE `FrameBudgetEventWire` — frame times, measured phase
 * time (scene/tween/render/react), the scripts Long Animation Frames named,
 * and what the scene held — at most once per `MIN_REPORT_INTERVAL_MS`.
 * The server records it as the `frame_budget_degraded` DomainLog event.
 *
 * Unfocused or hidden frames are skipped, not judged: the browser throttles
 * them on purpose (see FreezeEventTypes `isThrottledFrameGap`).
 *
 * Cost: one array push per frame; a sort + a scene walk once per window.
 */

import {
  FRAME_BUDGET_CONFIG,
  type FrameBudgetEventWire,
  type FrameBudgetFrameStats,
} from '../../shared/diagnostics/FrameBudgetTypes';

type WireContext = Pick<FrameBudgetEventWire,
  'route' | 'levelId' | 'focusClass' | 'mobCount' | 'playerCount' | 'scene' | 'heapUsedMB' | 'domNodes'>;

export interface FrameBudgetMonitorDeps {
  /** Wall clock for `capturedAt`. */
  wallNow(): number;
  /** Phase budget drained for the window (FramePhaseBudget.drain). */
  drainPhases(windowMs: number): { phase: string; msPerSec: number }[];
  /** React commits since the last call, or null when not measured. */
  drainReactCommits(): number | null;
  /** Scene/page context, read only when a report is built. */
  collectContext(): WireContext;
  /** Deliver a report. Must never throw into the frame loop. */
  send(event: FrameBudgetEventWire): void;
}

export interface LoafScriptLike {
  sourceFunctionName?: string;
  sourceURL?: string;
  invoker?: string;
  duration: number;
}

export interface LoafEntryLike {
  startTime: number;
  duration: number;
  scripts?: readonly LoafScriptLike[];
}

const round1 = (n: number): number => Math.round(n * 10) / 10;

function percentile(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) return 0;
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];
}

/**
 * Frame statistics over one window of frame deltas (ms). `fps` is over the
 * FOCUSED time the deltas cover, not the window's wall time: unfocused frames
 * are skipped, so dividing by the window turned "DJ clicked into OBS for six
 * seconds" into "19.9 fps" on a window whose frames ran at p50 16.7 ms
 * (prod tomb_waking, 2026-10-08, lane AL). `windowMs` is the fallback when no
 * delta was recorded.
 */
export function computeFrameStats(deltas: readonly number[], windowMs: number): FrameBudgetFrameStats {
  const sorted = [...deltas].sort((a, b) => a - b);
  let over33 = 0;
  let over50 = 0;
  let focusedMs = 0;
  for (const d of deltas) {
    if (d > 33.4) over33++;
    if (d > 50) over50++;
    focusedMs += d;
  }
  const spanMs = focusedMs > 0 ? focusedMs : windowMs;
  return {
    frames: deltas.length,
    fps: spanMs > 0 ? round1((deltas.length / spanMs) * 1000) : 0,
    p50Ms: round1(percentile(sorted, 0.5)),
    p95Ms: round1(percentile(sorted, 0.95)),
    p99Ms: round1(percentile(sorted, 0.99)),
    maxMs: round1(sorted.length ? sorted[sorted.length - 1] : 0),
    over33,
    over50,
  };
}

export function isFrameBudgetDegraded(stats: FrameBudgetFrameStats): boolean {
  if (stats.frames < FRAME_BUDGET_CONFIG.MIN_FRAMES) return false;
  return stats.p95Ms > FRAME_BUDGET_CONFIG.P95_BUDGET_MS
    || stats.over50 >= FRAME_BUDGET_CONFIG.MAX_FRAMES_OVER_50;
}

/** `fn @ file` for a LoAF script, with the URL reduced to its repo path. */
export function loafScriptName(s: LoafScriptLike): string {
  const file = (s.sourceURL ?? '').replace(/[?#].*$/, '').replace(/^.*\/(src|node_modules)\//, '$1/');
  const fn = s.sourceFunctionName || s.invoker || 'anonymous';
  return (file ? `${fn} @ ${file}` : fn).slice(0, 120);
}

/** Total script time per name across LoAF entries, longest first. */
export function aggregateLoafScripts(entries: readonly LoafEntryLike[], n = 5): { name: string; ms: number }[] {
  const totals = new Map<string, number>();
  for (const e of entries) {
    for (const s of e.scripts ?? []) {
      const name = loafScriptName(s);
      totals.set(name, (totals.get(name) ?? 0) + s.duration);
    }
  }
  return [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([name, ms]) => ({ name, ms: Math.round(ms) }));
}

export class FrameBudgetMonitor {
  private windowStart: number | null = null;
  private lastFrame: number | null = null;
  private deltas: number[] = [];
  private loaf: LoafEntryLike[] = [];
  private lastReportAt = Number.NEGATIVE_INFINITY;
  private observer: PerformanceObserver | null = null;

  constructor(private readonly deps: FrameBudgetMonitorDeps) {}

  /** Start collecting Long Animation Frames (Chromium 123+; no-op elsewhere). */
  start(): void {
    try {
      if (typeof PerformanceObserver === 'undefined'
        || !PerformanceObserver.supportedEntryTypes?.includes('long-animation-frame')) return;
      this.observer = new PerformanceObserver((list) => {
        for (const e of list.getEntries()) this.noteLoaf(e as unknown as LoafEntryLike);
      });
      this.observer.observe({ type: 'long-animation-frame', buffered: false });
    } catch {
      this.observer = null;
    }
  }

  stop(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  /** Bounded: a window holds at most a few hundred long frames. */
  noteLoaf(entry: LoafEntryLike): void {
    if (this.loaf.length < 512) this.loaf.push(entry);
  }

  /**
   * One rAF frame. Returns the report when this frame closed a degraded window
   * (also handed to `deps.send`), else null.
   */
  noteFrame(now: number, focused: boolean): FrameBudgetEventWire | null {
    if (this.windowStart === null) this.windowStart = now;
    if (!focused) {
      this.lastFrame = null;
    } else {
      if (this.lastFrame !== null) {
        const d = now - this.lastFrame;
        if (d > 0 && d < FRAME_BUDGET_CONFIG.PAUSE_CEILING_MS) this.deltas.push(d);
      }
      this.lastFrame = now;
    }
    const windowMs = now - this.windowStart;
    if (windowMs < FRAME_BUDGET_CONFIG.WINDOW_MS) return null;
    return this.closeWindow(now, windowMs);
  }

  private closeWindow(now: number, windowMs: number): FrameBudgetEventWire | null {
    const windowStart = this.windowStart ?? now;
    const stats = computeFrameStats(this.deltas, windowMs);
    // Drain every window so each report covers exactly its own window.
    const phaseBudgetMs = this.deps.drainPhases(windowMs);
    const commits = this.deps.drainReactCommits();
    const loaf = this.loaf.filter((e) => e.startTime >= windowStart);
    this.deltas = [];
    this.loaf = [];
    this.windowStart = now;

    if (!isFrameBudgetDegraded(stats)) return null;
    if (now - this.lastReportAt < FRAME_BUDGET_CONFIG.MIN_REPORT_INTERVAL_MS) return null;
    this.lastReportAt = now;

    const event: FrameBudgetEventWire = {
      capturedAt: this.deps.wallNow(),
      windowMs: Math.round(windowMs),
      frames: stats,
      phaseBudgetMs,
      reactCommitsPerSec: commits === null ? null : round1((commits / windowMs) * 1000),
      longFrames: loaf.length,
      loafScripts: aggregateLoafScripts(loaf),
      ...this.deps.collectContext(),
    };
    try {
      this.deps.send(event);
    } catch {
      // Diagnostic-only: never perturb the frame loop.
    }
    return event;
  }
}
